import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, of, firstValueFrom, lastValueFrom, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ConfigService } from '../../services/config.service';
import { AlertsService } from '../../services/alerts.service';
import { UserService } from '../../services/user.service';
import { MapService } from '../../map/services/map.service';
import { SearchResults, Criterion, SearchParams } from '../../models/search-objects.interface';
import { BlockUIService } from '../../layout/block-ui/block-ui.service';
import { Router, ActivatedRoute, NavigationStart, NavigationEnd  } from '@angular/router';
import { EventEmitter } from '@angular/core';
import dayjs from 'dayjs';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Location } from '@angular/common';
import cloneDeep from 'lodash/cloneDeep';
import { formatString } from '../../utils/string-utils';
import { RoutingService } from '../../services/routing.service';
import { NgZone, ApplicationRef } from '@angular/core';
import { filter, pairwise } from 'rxjs/operators';
import { timeout } from 'rxjs/operators';
import { HttpContext, HttpContextToken } from '@angular/common/http';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

declare let Terraformer: any; // terraformer-wkt-parser.js, https://github.com/esri/terraformer-wkt-parser

@Injectable({
  providedIn: 'root',
})

export class SearchService {
	broadcastEvent: EventEmitter<{ type: string; data: any; options?: any; facetsField?: string; timestamp?: Date }> = new EventEmitter();
	
  lastWFS: string | null = '';
  query: string | null = '';
  aoi: string | null = '';
  criteria: { [key: string]: Criterion } = {};
  pageIndex: number = 1;
  pageSize: number = 25;
  results: SearchResults | null = null;
  configurableParams: any = {};
  type: string = "16";
  sortField: string | null = '';
  sortDirection: string | null = '';
  sort: string | null = '';
  geometrySearchPredicate: string = "Intersects"; 
  browseSearchFilter: string | null = '';  
  validCollectionValueCache: any = {};
  resultView: string | undefined; 	
  mapDetached: boolean = false;
  libraries: string[] | undefined;
  records: any[] | null = null;
  lengthUnit: number | undefined;
  facet: string | null = '';
  isTransitionComplete: boolean = false;
  private lastProcessedUrl: string = '';
  browseConfig: any = {};
  exitStates: any = {};
  
  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private router: Router,
    private route: ActivatedRoute,
    private alertsService: AlertsService,
    private userService: UserService,
	private mapService: MapService,
    private sanitizer: DomSanitizer,
    private blockUI: BlockUIService,
    private location: Location,
    private ngZone: NgZone,
    private appRef: ApplicationRef,
    private routingService: RoutingService,
    private ngbModal: NgbModal
  ){
     if (this.configService.getConfig().esri && this.configService.getConfig().esri.searchPredicate) {
       this.geometrySearchPredicate = this.configService.getConfig().esri.searchPredicate;
     }
     this.initializeSvc();
     this.initOnBroadcast();
  }
  
  initOnBroadcast(){
	  this.router.events.subscribe(e => {
		    if (e instanceof NavigationStart) {
		      console.log('Navigation started to:', e.url);
		    }
		    if (e instanceof NavigationEnd) {
		      console.log('Navigation ended at:', e.url);
		    }
	  });
	  
      this.userService.broadcastParamsEvent.subscribe((args: any) => {
	  	if(args.type === 'autologin'){
			this.doAutoLoginSearch(args.data?.data ?? {}, args.data?.criteria ?? '');		  
		}
	  });
	  	  
	  this.userService.broadcastEvent.subscribe((eventId) => {
		switch (eventId) {
          case 'loggedout':
			this.resultView = undefined;  
            break;
          case 'userPrefsUpdate':
			this.libraries = this.userService.user?.properties?.awlibraryObjectList?.filter((x: any) => { return x.isAvailable }).map((x:any)=> { return x.name; });
	        if (this.userService.user?.loggedin && this.userService.user?.properties && this.userService.user?.properties?.awpropertyObjectList) {
	            for (const prop in this.userService.user?.properties?.awpropertyObjectList) {
	                let property = this.userService.user?.properties?.awpropertyObjectList[prop];
	                if (property.name == "NUMBER_DOCUMENTS_PER_PAGE") {
	                    this.pageSize = property.value;
	                    break;
	                }
	            };
	        };
            break;  
        }  
    });
	
	  this.mapService.mapServiceEvent.subscribe((event) => {
		  switch (event.eventType) {
			  case 'updateAOI':
				  this.aoi = event.data.aoi;
				  this.query = this.query == null ? "*" : this.query;
				  this.pageIndex = 1;
				  let params = { q: this.query, aoi: this.aoi };
				  params = this.validateSearchStateParams(params);
				  this.routingService.updateSearchUrl(params, true);
				  if (this.mapDetached) {
					  this.broadcastEvent.emit({ type: 'mapDetachSearch', data: { 'data': this.getSearchObject() } });
				  }
				  this.doSearch(null);
				  break;
				  
			  case 'updateGeometrySearchPredicate':
				  this.geometrySearchPredicate = event.data.value;
				  break;
		  }  
	});	

    this.broadcastEvent.subscribe((args: any) => {
      switch (args.type) {
       case 'userTriggeredSearchExecution':
		 this.executeUserTriggeredSearch(args.data);
         break;
       case 'userTriggeredCriteriaUpdate':
		 this.updateUserTriggeredCriteria(args.data, args.options);
         break;  
       case 'userTriggeredBrowseCriteriaUpdate':
		 this.updateUserTriggeredBrowseCriteria(args.data,args.options,args.facetsField);
         break;   
      } 	
      
    });
  }
 
  updateUserTriggeredBrowseCriteria(searchParms: any, options: any, facetFields:string) {
    options || (options = {});
    let criteria: string[] = [];
    if (facetFields) {
        this.browseSearchFilter = facetFields;
    } else {
        this.recurseSearchParms(searchParms, criteria);
    };

    let strung = criteria.join("~");
    this.pageIndex = 1;
    this.query = !this.query ? "*" : this.query
    this.type = this.type ? this.type : "16"
    this.geometrySearchPredicate = this.geometrySearchPredicate ? this.geometrySearchPredicate : this.configService.getConfig().esri.searchPredicate
    let sortParams = this.sort;
    if (options.sortDirection && options.sortField) {
        sortParams = options.sortField + ":" + options.sortDirection;
    }
    let params = { q: this.query, criteria: searchParms == null ? null : strung, aoi: this.aoi, facet: this.facet, sort: sortParams, type: this.type };
	params = this.validateSearchStateParams(params);
    if (!options.keepState) {
		/*this.router.navigate(['search'], {
	    queryParams: params,
        relativeTo: this.route
	  });*/
	  this.routingService.updateSearchUrl(params, true);
    }
    this.processParams(params);
    if (this.mapDetached) {
        this.broadcastEvent.emit({type:'mapDetachSearch', data : {'data':this.getSearchObject()} });
    };
    this.doSearch({ "libraries": options.libraries });
  };                     

 updateUserTriggeredCriteria(searchParms: any, options: any) {
    options || (options = {});
    let criteria: string[] = [];
    this.recurseSearchParms(searchParms, criteria);

    let strung = criteria.join("~");
    this.pageIndex = 1;
    this.query = !this.query ? "*" : this.query;
    if (options.query) {
        this.query = options.query;
    }
    this.type = this.type ? this.type : "16";
    if (options.queryType) {
        this.type = options.queryType;
    }
    this.geometrySearchPredicate = this.geometrySearchPredicate ? this.geometrySearchPredicate : this.configService.getConfig().esri.searchPredicate;
    let sortParams = this.sort;
    if (options.sortDirection && options.sortField) {
        sortParams = options.sortField + ":" + options.sortDirection;
    }
    let params = { q: this.query, criteria: searchParms == null ? null : strung, aoi: this.aoi, facet: this.facet, sort: sortParams, type: this.type, libraries: searchParms.libraries };
	params = this.validateSearchStateParams(params);

	if (options.changeState) {
		// navigate reload
		//this.router.navigate(['search'], { queryParams: params }); 
		this.routingService.updateSearchUrl(params, false);
	} else {
	  this.routingService.updateSearchUrl(params, true);
	  /*this.router.navigate(['search'], {
	    queryParams: params,
        relativeTo: this.route
	  });*/
	}
      
    this.processParams(params);
    if (this.mapDetached) {
  	  this.broadcastEvent.emit({type:'mapDetachSearch', data : {'data':this.getSearchObject()} });
    };
    this.doSearch({ libraries: options.libraries });
  };
  
 executeUserTriggeredSearch(args: any) {
    if (!args || !args.searchObj) return;

    let searchParams: any = {};
    try {
      searchParams = JSON.parse(args.searchObj.data);
    } catch (e) {
      return;
    }

    if (!searchParams.query) searchParams.query = "*";

    let validFields: { [key: string]: any } = {};
    // Assuming `userService` is available in this component/service
    this.userService.user.properties.awfieldObjectList.forEach((x: any) => {
      if (x.isAvailable) validFields[x.name] = x;
    });

    let criteriaString = "";
    for (let f in searchParams.criteria) {
      if (f === 'facet-fields') {
        this.browseSearchFilter = searchParams.criteria[f].searchParam;
      } else {
        let field = searchParams.criteria[f];
        let normalized = f.replace(/_ss$/, "").replace(/_s$/, "");
        if (field.searchParam && validFields[normalized]) {
          criteriaString = criteriaString ? `${criteriaString}~${field.searchParam}` : field.searchParam;
        } else {
		  //do not run the search if the user does not have access to all the fields in the search
          this.alertsService.error({
            title: "Error running search.",
            message: "Search cannot be run due to missing fields.",
            delay: 4000
          });
          console.error("Search cannot be run due to missing fields.");
          return;
        }
      }
    }
    setTimeout(() => {
      if (args.notify) this.clearSearches();
    }, args.notify ? 100 : 0);

    setTimeout(() => {
      let params: any = { q: searchParams.query, criteria: criteriaString, aoi: searchParams.aoi, type: String(searchParams.type) };

      if (Array.isArray(searchParams.libraries)) {
        params.libraries = searchParams.libraries.join(" OR ");
      } else {
        params.libraries = Object.keys(searchParams.libraries).join(" OR ");
      }

      this.processParams(params);
      this.broadcastEvent.emit({type:'SearchParmsUpdated',data:{}});
      params = this.validateSearchStateParams(params);
      // Assuming you are using Angular Router
      // navigate reload
      //this.router.navigate(['search'], { queryParams: params });
      this.routingService.updateSearchUrl(params, false);
      this.instantiateSearchParms(params);
      if (this.mapDetached && args.broadcastSync) {
  	    this.broadcastEvent.emit({type:'mapDetachSearch', data : {'data':this.getSearchObject()} });
      }
    }, args.notify ? 100 : 0);
  }
  
  initializeSvc() {
    this.lastWFS = null;
    this.query = null;
    this.aoi = null;
    this.criteria = {};
    this.pageIndex = 1;
    if (!this.pageSize) {
        this.pageSize = 25;
    };
    this.results = null;
    this.configurableParams = {};
    this.type = this.type ? this.type : "16";
    if (!this.geometrySearchPredicate) {
        if (this.configService.getConfig().esri && this.configService.getConfig().esri.searchPredicate) {
            this.geometrySearchPredicate = this.configService.getConfig().esri.searchPredicate;
        } else {
            this.geometrySearchPredicate = "Intersects"
        }
    }
    this.browseSearchFilter = null;
  };
  
  ORMatch(value: string, content: string): boolean {
    if (!content || !value) {
        return false;
    }
    var parts = value.split(" OR ");
    if (parts && parts.length > 1) {
        return !!(parts.find((x) => { return x.toLowerCase() == content.toLowerCase() }));
    }
    return false;
  }
  
  setBrowseSearchFilter(value: string): void {
    this.browseSearchFilter = value;
  }
  
	updateValue(currInput: any, criterion: any): string | null {
		let parentMask = null;
	    if (!currInput) return parentMask;

	    switch (currInput.type.toUpperCase()) {  // Normalize case handling
	        case "LONG":
	        case "INTEGER":
	        case "FLOAT":
	        case "RADIO":
	        case "DATE":
			case "DATE_RANGE":
	            currInput.content = criterion?.content ?? null;
	            break;

	        case "STRING":
	            currInput.list = criterion?.content == null ? [{ content: null }] : criterion.content;
	            break;

	        case "LIST":
	            currInput.content = null;
	            if (!criterion?.content || !currInput.options) return parentMask;

	            const criterionTest = criterion.content.replace(/['"]+/g, '').toLowerCase();
	            currInput.content = currInput.options.filter((option: any) =>
	                option &&
	                (option.value.toLowerCase().replace(/['"]+/g, '') === criterionTest ||
	                    this.ORMatch(criterionTest, option.value.replace(/['"]+/g, '')))
	            );
	            currInput.content = currInput.content.length > 1 ? currInput.content : currInput.content[0] ?? null;
	            break;

	        case "LIST_MULTI":
	            currInput.content = [];
	            if (!criterion?.content || criterion.content.length < 1 || !currInput.options) return parentMask;

	            currInput.content = currInput.options.filter((option: any) =>
	                option &&
	                criterion.content.some((currCriterion:string) => {
	                    const optionValue = option.value.replace(/['"]+/g, '').toLowerCase();
	                    const criterionValue = currCriterion.replace(/['"]+/g, '').toLowerCase();
	                    return optionValue === criterionValue || this.ORMatch(criterionValue, optionValue);
	                })
	            );
	            break;
	    }
	    return parentMask;
	}

  isValidCollectionValue(field: string, fieldValue: string | any[]): boolean {
    let value = "";
    if (Array.isArray(fieldValue) && fieldValue.length > 0) {
        if (Array.isArray(fieldValue[0]) && fieldValue[0].length > 0) {
            value = String(fieldValue[0][0]);
        } else {
            value = Array.isArray(fieldValue[0])? "" : String(fieldValue[0]);
        }
    } else {
        value = Array.isArray(fieldValue) ? "" : String(fieldValue);
    }
        
    if (!this.validCollectionValueCache[field]) {
        this.validCollectionValueCache[field] = [];
    }

    let fvals = this.validCollectionValueCache[field];
    let fval = fvals.find((f: any) => f.value === value);

    if (!fval) {
        console.log("valid collection value lookup");

        fval = { value, valid: true }; // Default to valid: true

        const collection = this.userService.user.properties.awfieldCollectionObjectList
            ?.find((x: any) => x.field === field);

        if (collection?.element) {
            const normalizedValue = value.toUpperCase();
            fval.valid = collection.element.some((y:any) => y.value.toUpperCase() === normalizedValue);
        }

        fvals.push(fval);
    }

    return fval.valid;
  };
  
  isValidWkt(aoi: string): boolean {
    if (!aoi)
        return false;
    if (aoi.indexOf("CIRCLE") == 0) {
        let beg = aoi.indexOf("(");
        let end = aoi.indexOf(")");
        if (beg > 0 && end > 0) {
            let sub = aoi.substring(beg + 1, end);
            let params = sub.split(/[\s,]+/);
            //y;
            let y = Number(params[0]);
            if (isNaN(y) || y > 90 || y < -90)
                return false;
            //x;
            let x = Number(params[1]);
            if (isNaN(x) || x > 180 || x < -180)
                return false;
            //d;
            let dia = params[2].split("=");
            if (!(dia.length > 1))
                return false;
            let d = Number(dia[1]);
            if (isNaN(d))
                return false;

            //all passed;
            return true;
        };
        return false;
    } else {
        try {
            let geometry = Terraformer.WKT.parse(aoi); //parser(aoi);
            return true;
        } catch (error) {
			console.error("Error parsing WKT:", error);
        }
    };

    return false;
  }
  
  clearSearches(isNotify: boolean = false, isupdateSearchUrl: boolean = true): void {
    this.lastWFS = null;
    this.query = null;
    this.aoi = null;
    this.criteria = {};
    this.aoi = '';
    this.browseSearchFilter = '';
    this.pageIndex = 1;
    this.results = null;
    this.pageIndex = 1;
    
    if(isupdateSearchUrl){
	    this.routingService.updateSearchUrl({ 
	    q: null, 
	    criteria: null, 
	    aoi: null, 
	    sort: null, 
	    type: this.type, 
	    browseconfig: null, 
	    timestamp: `${new Date().getTime()}` //new Date().getTime() // Change this param every time to trigger reload
	  }, false, isNotify);
	}
	
	this.instantiateSearchParms({ 
	    q: null, 
	    criteria: null, 
	    aoi: null, 
	    sort: null, 
	    type: this.type, 
	    browseconfig: null, 
	    timestamp: `${new Date().getTime()}` //new Date().getTime() // Change this param every time to trigger reload
	  });  
  }; 
  
updateThenSearch(params: any): void {
    // Update service properties dynamically
    let tempQuery: any = {
    query: '',
    type: '',
    resultView: 'G',
    };
    Object.keys(params).forEach((param) => {
      tempQuery[param] = params[param];
    });

    // Prepare updated search parameters
    let updated = { q: tempQuery.query, type: tempQuery.type };
    updated = this.validateSearchStateParams(updated);

    // Navigate without reloading the component (similar to `{ notify: false }` in AngularJS)
    /*this.router.navigate(['search'], {
        queryParams: updated,
        relativeTo: this.route
    });*/
    this.routingService.updateSearchUrl(updated, true);
    this.doSearch({ libraries: params.libraries });
}

validateSearchStateParams(params: any = {}): any {
    // Always include a view property
    if (!params.hasOwnProperty('v') || params.v === undefined) {
      let v = 'G'; // Default value
      // Check the route parameters
      const stateV = this.getSearchQueryParam('v');
      if (stateV) {
        v = stateV;
      } else if (this.resultView) {
        v = this.resultView;
      } else if (this.userService?.user?.properties) {
        for (const prop in this.userService.user.properties.awpropertyObjectList) {
          const property = this.userService.user.properties.awpropertyObjectList[prop];
          if (property.name === 'RESULT_LIST_DISPLAY_TYPE') {
            switch (property.value) {
              case '1':
                v = 'G';
                break;
              case '2':
                v = 'L';
                break;
              case '3':
                v = 'T';
                break;
              case '4':
                v = this.configService.getConfig()?.esri?.enabled ? 'M' : 'G';
                break;
              default:
                v = 'G';
            }
          }
        }
      }

      // Set the value of 'v' in params
      params.v = v;
    }
    return params;
  }
  
  processParams(params: any): void {
    Object.keys(params).forEach((param) => {
      switch (param) {
        case 'q':
          this.query = params.q;
          break;

        case 'sort':
          if (params.sort) {
            const sorts = params.sort.split(':');
            if (sorts.length !== 2) return;
            this.sortField = sorts[0];
            this.sortDirection = sorts[1].toUpperCase() === 'ASC' ? 'ASC' : 'DESC';
          }
          break;

        case 'type':
          this.type = params.type;
          break;

        case 'aoi':
          if (params.aoi && this.isValidWkt(params.aoi)) {
            this.aoi = params.aoi;
			this.mapService.broadcastRedrawAOIEvent({aoi: params.aoi });
          } else {
            this.aoi = null;
            if(params.aoi){
              params.aoi = null;
            }
            // Handle invalid AOI case (display error message)
          }
          break;

        case 'criteria':
          this.processCriteria(params.criteria);
          break;
      }
    });
  }
  
  private processCriteria(criteria: string): void {
    this.criteria = {}; // Reset criteria object
    this.processBrowseSearchFilter();
    if (!criteria) return;  
    const splitCriteria: string[] = criteria.split('~');
    splitCriteria.forEach((criterion) => {
      const [header, info] = criterion.split('=');
      if (!header || !info) return;

      // Example of getting user preferences
      let critDef = this.userService?.user?.systemPreferences?.find((x: any) => {
            return x.name == header || x.name + "_s" == header || x.name + "_ss" == header;
        });
      if (critDef && critDef.length > 0) {
        critDef = critDef[0];
      } else {  
	     if (!critDef && this.userService.user?.browseFields?.includes(header)) {
		   // add missing browse fields to the search criteria;
		   critDef = { dcname: header, displayName: header.toUpperCase(), type: 'STRING' }
	     }
	  }   
      if (critDef) {
        this.processCriterion(header, info, critDef, splitCriteria, criteria, criterion);
      }
    });
  }
  
  private processBrowseSearchFilter(): void {
	  if (this.browseSearchFilter) {
        let userInput = decodeURI(this.browseSearchFilter);
        if (userInput.length > 0) {
            let display_value = "";
            let paths = userInput.split('>>');
            paths.forEach(path => {
                let field = path.split(":")[1];
                if (field !== "{empty}") {
                    display_value += field + ">>";
                }
            })
            if (display_value.endsWith(">>")) {
                display_value = display_value.substring(0, display_value.length - 2);
            }
            this.criteria["facet-fields"] = {
			  searchParam: `facet-fields=${userInput}`,
			  searchString: `${"facet-fields"}=[${userInput}]`, // Equivalent to "{0}=[{1}]".format(...)
			  content: userInput,
			  display_value: display_value,
			  display_header: "Browse Path"
			};
        };
    }
  }  
  
  private processCriterion(header: string, info: string, critDef: any, splitCriteria: string[], criteria: string, criterion: string): void {
    if (critDef.AWListObject?.type === 'LIST') {
      this.processListCriterion(header, info, critDef, splitCriteria, criteria, criterion);
    } else if (critDef.type === 'LIST_MULTI') {
      this.processMultiListCriterion(header, info, critDef, criterion);
    } else if (critDef.type === 'DATE' || critDef.type === 'DATE_RANGE') {
      this.processDateCriterion(header, info, critDef, criterion);
    } else if (critDef.type === 'STRING') {
      this.processStringCriterion(header, info, critDef);
    } else if (['LONG', 'INTEGER', 'FLOAT'].includes(critDef.type)) {
      this.processNumericCriterion(header, info, critDef);
    }
  }
  
  private processListCriterion(header: string, info: string, critDef: any, splitCriteria: string[], criteria: string, criterion: string): void {
    let parentMaskValue: string | null = null;

    // Extract parent mask value if parent exists and is in criteria
    if (critDef.parent && criteria.includes(critDef.parent.name + '=')) {
        for (const localCriterion of splitCriteria) {
            const parentHeader = localCriterion.substring(0, localCriterion.indexOf('='));
            if (parentHeader === critDef.parent.name) {
                parentMaskValue = localCriterion.substring(localCriterion.indexOf('=') + 1);
                break;
            }
        }
    }

    // Filter the list object elements
    const matchedObject = critDef.AWListObject.element.find((item: any )=> {
        if (!item) return false;

        const itemValue = (item.value ? item.value.toUpperCase() : item.toUpperCase());
        const matchesInfo = itemValue === info.toUpperCase();
        const matchesParentMask = !parentMaskValue || !item.parentMask || item.parentMask.toUpperCase() === parentMaskValue.toUpperCase();

        return matchesInfo && matchesParentMask;
    }); // Get the first matching object

    // Determine search string value and content
    const isAddQuotes = header !== "load_num";
    let searchStringValue = matchedObject ? matchedObject.value : info;
    const searchStringContent = matchedObject ? matchedObject.value : info;

    // Add quotes if necessary
    if (isAddQuotes && !(searchStringValue.startsWith('"') && searchStringValue.endsWith('"'))) {
        searchStringValue = `"${searchStringValue}"`;
    }

    // Update service criteria
    this.criteria[header] = {
        searchParam: criterion,
        searchString: `${header}=[${searchStringValue}]`,
        content: searchStringContent,
        display_value: searchStringValue,
        display_header: critDef.displayName
    };
  }
  private processMultiListCriterion(header: string, info: string, critDef: any, criterion: string): void {
	  let userInput = info.split("|");
      let properValues = [];
	  for (const libraryElement of  critDef.AWListObject.element) {	  
        var inputObject = userInput.find((y: string)=> { return y == libraryElement.value; });
        if (inputObject)
            properValues.push(inputObject);
      }
      if (properValues.length > 0) {
        let isAddQuotes = ("load_num" !== header);
        if (isAddQuotes) {
            this.criteria[header] = { 
				searchParam: criterion, 
				searchString: `${header}=[${'"' + properValues.join('" OR "') + '"'}]`, 
				content: properValues, 
				display_value: '"' + properValues.join("\" OR \"") + '"', 
				display_header: critDef.displayName 
				}
        } else {
            this.criteria[header] = {
				 searchParam: criterion, 
				 searchString: `${header}=[${properValues.join('" OR "')}]`, 
				 content: properValues, display_value: properValues.join(" OR "), 
				 display_header: critDef.displayName 
				 }
        }
      }
  }
	  
  private processDateCriterion(header: string, info: string, critDef: any, criterion: string): void {
    let userInput = info.split('|');
    let property = this.userService.user.properties?.awpropertyObjectList?.find((x: any) => { return x.name == "USER_INTERFACE_DATE_TYPE"; });
    let dateFormatDisplay = "MM/DD/YY";
    if (property) {
        dateFormatDisplay = property.value;
    };
    const dateFormat = "MM/DD/YYYY";                                    
    if (userInput.length > 2) {
	  let properValues = [];
	  for (let i = 0; i < userInput.length; i += 2) {
   	    let startString = userInput[i];
        let endString = userInput[i + 1];
        let startDate = dayjs.unix(Number(userInput[i]));
        let endDate = dayjs.unix(Number(userInput[i + 1]));
        if (startDate.isValid() && endDate.isValid() && startDate < endDate) {
            let dateString = startDate.format(dateFormat) + "-" + endDate.format(dateFormat);
            properValues.push(dateString);
        } 	 
	  }	
	  this.criteria[header] = { 
		  searchParam: criterion, 
		  searchString: `${header}=[${'"' + properValues.join('" OR "') + '"'}]`, 
		  content: properValues, display_value: properValues.join(" OR "), 
		  display_header: critDef.displayName 
	    }
    } else if (userInput.length == 2) {
		const startDate = dayjs.unix(Number(userInput[0]));
        const endDate = dayjs.unix(Number(userInput[1]));
        if (startDate.isValid() && endDate.isValid() && startDate < endDate) {
          this.criteria[header] = { searchParam: criterion, 
            searchString: `${header}=[${startDate.format(dateFormat) + "-" + endDate.format(dateFormat)}]`, 
            content: { startDate: startDate, endDate: endDate }, 
            display_value: startDate.format(dateFormatDisplay) + "-" + endDate.format(dateFormatDisplay), 
            display_header: critDef.displayName 
          }
        }
	} else {
       //check if the delimiter is -
       //var userInput = info.split(/^([\d/-]+)?\s?-?\s?([\d/-]+)?/).filter(x => x !== '');
       userInput = info.split("-");
       if (userInput.length == 2) {
            const startDate = dayjs(userInput[0]);
            const endDate = dayjs(userInput[1]);
            this.criteria[header] = { 
				searchParam: criterion, 
				searchString: `${header}=[${startDate.format(dateFormat) + "-" + endDate.format(dateFormat)}]`, 
				content: { startDate: startDate, endDate: endDate }, 
				display_value: startDate.format(dateFormatDisplay) + "-" + endDate.format(dateFormatDisplay), 
				display_header: critDef.displayName 
			}
       } else if (userInput.length == 6) {
		   const startDateParts = userInput.slice(0, 3); // Extract first 3 elements without modifying the array
           const endDateParts = userInput.slice(3, 6); // Extract next 3 elements without modifying the array
		   // Parse dates using dayjs
		   const startDate = dayjs(startDateParts.join("-"));
		   const endDate = dayjs(endDateParts.join("-"));

           if (startDate.isValid() && endDate.isValid() && startDate.isBefore(endDate)) {
				this.criteria[header] = { 
					searchParam: criterion, 
					searchString: `${header}=[${startDate.format(dateFormat) + "-" + endDate.format(dateFormat)}]`, 
					content: { startDate: startDate, endDate: endDate }, 
					display_value: startDate.format(dateFormatDisplay) + "-" + endDate.format(dateFormatDisplay), 
					display_header: critDef.displayName 
				}			   
		   }
            
       } else {
            this.criteria[header] = { 
				searchParam: criterion, 
				searchString: `${header}=[${userInput}]`,
				content: { startDate: userInput[0], endDate: userInput[0] }, 
				display_value: userInput[0], 
				display_header: critDef.displayName 
			}
       }       

	}
  }
  
  private processStringCriterion(header: string, info: string, critDef: any): void {
    const userInput = info.split('|').map(decodeURIComponent);
    if (userInput.length > 0) {
	    this.criteria[header] = {
	      searchParam: `${header}=${info}`,
	      searchString: `${header}=[${userInput.join(' OR ')}]`,
	      content: userInput,
	      display_value: userInput.join(' OR '),
	      display_header: critDef.displayName
	    };
    }
  }
  
 private processNumericCriterion(header: string, info: string, critDef: any): void {
    if(info){
	    this.criteria[header] = {
	      searchParam: `${header}=${info}`,
	      searchString: `${header}=[${info}]`,
	      content: info,
	      display_value: info,
	      display_header: critDef.displayName
	    };
    }
  }
  
  instantiateSearchParms(params: any): void {
    if (!params || !params.q && !params.aoi && !params.criteria && !params.sort) {
        return;
    }
    this.processParams(params);

    if (!this.query) {
        this.query = "*";
        params.q = this.query;
    }

    // All controls dependent on searchparms should refresh their data
    this.broadcastEvent.emit({type:'SearchParmsUpdated',data:{}});
    console.log("searchParmsUpdated");
	params = this.validateSearchStateParams(params);
	this.routingService.updateSearchUrl(params, true);
	let p: { libraries?: string[] } = {};
	if (Array.isArray(params.libraries)) {
	    p.libraries = params.libraries;
	} else if (typeof params.libraries === 'string' && params.libraries.trim().length > 0) {
	    p.libraries = params.libraries.split(",");
	}
    // search synchronization issue for autologin with browse init. do not execute initial search. search will be executed from browse configuration
    if (params.criteria?.includes('initial-browse-path')) {
        console.log("do not execute instantiateSearchParms search");
    } else {
        this.doSearch(p);
    }
  }
  
  update_pagingSorting(parms: any) {
	let update = {};
    if (!parms) {
        this.sortField = null;
        this.sortDirection = null;
        this.pageIndex = 1;
		update = { sort: null };
    } else {
        this.sortField = parms.sortField;
        this.sortDirection = parms.sortDirection;
        this.pageIndex = parms.pageIndex;
        this.pageSize = parms.pageSize;
        let sorty = this.sortDirection != null && this.sortField != null ? this.sortField + ":" + this.sortDirection : null;
		update = { sort: sorty };
    }
    this.routingService.updateSearchUrl(update, false);
    this.broadcastEvent.emit({type:'sortChanged', data:{ direction: this.sortDirection, field: this.sortField }});

    if (this.results) {
        this.doSearch({ invoker: "SYSTEM", libraries: parms.libraries });
        if (this.mapDetached) {
			this.broadcastEvent.emit({type:'mapDetachSearch', data : {'data':this.getSearchObject()} });
        }
    }
  };
       
  buildQuery(query: string | null, aoi: string | null, criteria: string | null, type: string | null, 
  libraries: string[] | null, isEncode: boolean = true, startQuery:string='') {
    query = query ? query : this.query

    //eventually will allow users to choose boolean,pattern, etc, for now just 16;
    // var searchtype =  configService.getConfig().search.searchType ? configService.getConfig().search.searchType : '16'
    let searchtype = type ? type : (this.type ? this.type : '16')
    let library = libraries ? libraries : (this.libraries ? this.libraries : this.configService.getConfig().search?.libraries)

    let str = startQuery+'qs=[' + query + '],queryType=[' + searchtype + '],library=[' + library.join(" OR ") + ']';
    if (criteria || criteria == "") {
        str += "," + criteria;
    } else if (this.criteria) {
		for (const f of Object.keys(this.criteria)) {
		  let field = this.criteria[f];
		  if (field?.searchString) {
		        str += "," + field.searchString;
		  }
		}
    };
    if(isEncode){
    	str = encodeURIComponent(str);
    }
    if (aoi || aoi == "") {
        str += ",geo=[" + aoi + "],geosearch_predicate=[" + this.geometrySearchPredicate + "]";
    }
    else if (this.aoi) {
        aoi = this.aoi;
		/*
		* Expects a WKT string in the form of: CIRCLE(center.y, center.x d=radiusInMeters)
		* Where the center point is in geographic coordinates
		* and the radius is in meters
		*/
        if (this.aoi.toLowerCase().indexOf("circle") > -1) {
            let comp = aoi.toLowerCase().replace("circle(", "").replace(")", "").split(/[\s,]+/);
            let y = comp[0];
            let x = comp[1];
			// WKT is expecting incoming d=, but actual value is not diameter but RADIUS
			// This seems to be assuming the incoming d= value is in KM and in degrees
			// In reality, it is in METERS calcuated using points in Web Mercator units
			// * 111.32 -- Is it trying to reverse a degree calculation?
			// Let's make any calculations before we get to this point!
            //var d = comp[2].replace("d=", "") * 1000 * 111.32;  // No!
			let d = comp[2].replace("d=", ""); // Client takes responsibility to create accurate WKT representation
            str += ",field61_num=[" + x + "],field63_num=[" + y + "],radius_num=[" + d + "],radius_num-type=[meters],geosearch_predicate=[" + this.geometrySearchPredicate + "]";
        }
        else {
            str += ",geo=[" + this.aoi + "],geosearch_predicate=[" + this.geometrySearchPredicate + "]";
        };

    };

    return str;
};

buildWfs(bbox: string): string {
    let str = this.configService.getConfig().search?.wfsURL;
    str += "q=" + encodeURIComponent(btoa(this.buildQuery(null, null, null, null, null, false, 'AWQL_FORM ')));
    str += "&attributeslist=common.symbol_code,symbol_code,title,library,product_size,extension&version=1.1.0&service=WFS&typename=aw_cluster_polygon&request=GetFeature&GEOHASH_DIFF=0&bbox=" + bbox + "&_=" + new Date().getTime(); //-120.1898288727,30.683782754722,-113.30689430238,34.660068024694;
    return str;
};

buildWS (uuid: string): string {
    let str = this.configService.getConfig().search.wsURL;
    str += "q=" + encodeURIComponent(btoa(this.buildQuery(null, null, null, null, null, false, 'AWQL_FORM ')));
    let params = new HttpParams();
    const wsParams = this.configService.getConfig().search?.wsParams;
	const timestamp = new Date().getTime();
    str += "&" + this.getHttpParams(wsParams) + `&ID=${uuid}&_=${timestamp}`;
    return str;
};

getRecord(uuid: string | number): Promise<any> {
    let recordURL = this.buildSearch(this.configService.getConfig().search.recordURL, this.configService.getConfig().search.recordParms, 1, 1, [], "*", "", "", "", "", []);
    recordURL += "&id=" + uuid;
    recordURL += "&_=" + new Date().getTime();
    return new Promise<any>((resolve, reject) => {
        this.http.get<any>(recordURL)
        .pipe(
          catchError(err => {
          reject("Failed to get record...");
          return of(null);
         })
        ).subscribe((response: any) => {
			if (response && response["ns34:ExceptionReport"]) {
                reject(response);
            }
            resolve(response);
        });
	});
};

	getWFS(bbox: string): Promise<string> {
		let wfsURL = this.buildWfs(bbox);
		this.lastWFS = wfsURL;

		return firstValueFrom(this.http.get(wfsURL, { responseType: 'text' }))
			.then((response: string) => {
				return response;
			})
			.catch((error: any) => {
				throw error;
			});
	}
        
 buildSearch(
    url: string | null,
    params: any,
    pageIndex: number | null,
    pageSize: number | null,
    additionalConstraints: string[] | null,
    query: string | null,
    aoi: string | null,
    criteria: string | null,
    sort: string | null,
    type: string | null,
    libraries: string[] | null
  ): string {
    if (!pageIndex) {
      pageIndex = this.pageIndex;
    }
    if (!pageSize) {
      pageSize = this.pageSize;
    }

    let str = url || this.configService.getConfig().search.searchURL;

    // Construct the constraints string
    const queryString = this.buildQuery(query, aoi, criteria, type, libraries, false);
    str += 'CONSTRAINT=' + encodeURIComponent(btoa(queryString + (additionalConstraints ? `,${additionalConstraints.join(',')}` : '')));

    // Add parameters to the URL
    const searchParams = params || this.configService.getConfig().search.searchParms;
    str += '&' + decodeURIComponent(this.getHttpParams(searchParams));
    // Add pagination
    str += `&STARTPOSITION=${(pageIndex - 1) * pageSize + 1}&MAXRECORDS=${pageSize}`;

    // Add sorting logic
    if (sort) {
      str += sort;
    } else if (this.sortDirection && this.sortField && this.sortField !== 'Default') {
      str += `&SORTBY=${this.sortField}:${this.sortDirection}`;
    } else if (this.userService.user?.properties?.awsortObjectList?.length > 0) {
      this.userService.user.properties.awsortObjectList.forEach((sortObj: any, i: number) => {
        const sortStr = `${sortObj.fieldName || 'AW5_SORT_BY_FINE_RANK'}:${sortObj.isAscending ? 'ASC' : 'DESC'}`;
        if (i > 0) {
          str += `,${sortStr}`;
        } else {
          str += `&SORTBY=${sortStr}`;
        }
      });
    }

    return str;
  }
  
  clearSearch(): void {
    this.records = null;
  }

  removeFacetFlags(params: any): void {
    if (!params) { return; }
    for (const prop in params) {
        if (params[prop] && typeof params[prop] === 'object') {
            delete params[prop].solrstring;
        }
    }
  }
  
  searchManually(pageIndex: number, pageSize: number, constraints: string[], query: string, aoi: string, criteria: string, sort: string, type: string, libraries: string[], isProcessParams: boolean): void {
    if (isProcessParams) {
        let params = { q: query, criteria: criteria, aoi: aoi, type: type };
		params = this.validateSearchStateParams(params);
        /*this.router.navigate(['search'], {
	        queryParams: params,
            relativeTo: this.route
	    });*/
	    this.routingService.updateSearchUrl(params, true);
        this.processParams(params);
    };
    let str = this.buildSearch(null, null, pageIndex, pageSize, constraints, null, null, criteria, sort, null, libraries);
    this.http.get<any>(str).subscribe({
      next: (response) => {
        const allowedQueryTypes = [3, 16, 32, 64];
        const results = response?.["csw:GetRecordsResponse"]?.["csw:SearchResults"];

        this.results = {
          total: results?.numberOfRecordsMatched ?? 0,
          geo_total: 0,
          count: this.pageSize,
          page: this.pageIndex,
          query: this.query,
          aoi: this.aoi,
          show_rank: !this.configService.getConfig().search.hideRank && allowedQueryTypes.includes(Number(this.type)),
          facets: results?.facetFields ?? [],
          records: results?.iStoreRecord 
            ? Array.isArray(results.iStoreRecord) 
              ? results.iStoreRecord 
              : [results.iStoreRecord] 
            : null
        } as SearchResults;

        this.formatSearchResults(this.results);
		this.broadcastEvent.emit({ type: 'searchEnded', data: this.results, timestamp: new Date() });
        //this.searchEnded.next(this.results);
      },
      error: () => {
		 this.broadcastEvent.emit({ type: 'searchEnded', data: { error: true }, timestamp: new Date() });  
        //this.searchEnded.next({ error: true });
      }
    });
  };

 searchWithGeoHash(geoHash: string, pageIndex: number, pageSize:number): Promise<SearchResults> {
    var str = this.buildSearch(null, null, pageIndex, pageSize, [`geohash=[*_${geoHash}]`],null, null, null, null, null, null);
    return new Promise<any>((resolve, reject) => {
        this.http.get<any>(str)
        .pipe(
          catchError(err => {
          reject("Failed to search on geohash ...");
          return of(null);
         })
        ).subscribe((response: any) => {
			const results = response["csw:GetRecordsResponse"]["csw:SearchResults"];
			const georesults:SearchResults = {
                total: results.numberOfRecordsMatched,
                count: results.numberOfRecordsReturned,
                page: pageIndex,
                records: results.iStoreRecord == null ? null : Array.isArray(results.iStoreRecord) ? results.iStoreRecord : [results.iStoreRecord]
            };
            
            resolve(georesults);
        });
	});
  };
  
  doSearch(parms: any | null): void {
    // Set the search parameters
    this.pageSize = parms && parms.pageSize ? parms.pageSize : this.pageSize;
    this.pageIndex = parms && parms.pageIndex ? parms.pageIndex : this.pageIndex;
    this.libraries = parms && parms.libraries ? parms.libraries : this.userService.user.properties.awlibraryObjectList.filter((x: any) => x.isAvailable).map((x: any) => x.name);

    let str = this.buildSearch(null, null, this.pageIndex, this.pageSize, null ,null, null, null, null, null, this.libraries || null);

    if (parms && parms.invoker) {
      str += "&INVOKER=" + parms.invoker;
    }
    this.broadcastEvent.emit({type:'searchStarted',data:{}});
    setTimeout(() => {
       this.blockUI.start(this.userService.LOCALIZATION.searching);
    });
    // Set up the HTTP request headers
    const headers = new HttpHeaders({
      "Accept": "application/json; charset=UTF-8",
      "Content-Type": "application/json; charset=UTF-8"
    });

    // Make the HTTP GET request
    this.http.get(str, { headers }).pipe(
      catchError(error => {
		this.broadcastEvent.emit({ type: 'searchEnded', data: { error: true }, timestamp: new Date() });
        this.blockUI.stop();
        throw error; // Re-throw the error after handling it
      })
    ).subscribe((response: any) => {
      const allowedQueryTypes = [3, 16, 32, 64];
      const results = response['csw:GetRecordsResponse']['csw:SearchResults'];

      // Set the results object
      this.results = {
        total: results.numberOfRecordsMatched,
        geo_total: 0,
        count: this.pageSize,
        page: this.pageIndex,
        query: this.query,
        aoi: this.aoi,
        show_rank: !this.configService.getConfig().search.hideRank && allowedQueryTypes.includes(Number(this.type)),
        facets: results.facetFields ? results.facetFields : [],
        records: results.iStoreRecord == null ? null : Array.isArray(results.iStoreRecord) ? results.iStoreRecord : [results.iStoreRecord]
      } as SearchResults;

      this.formatSearchResults(this.results);
	  this.broadcastEvent.emit({ type: 'searchEnded', data: this.results, timestamp: new Date() });
      this.blockUI.stop();
    });
  }
  
  doSearchDashboard(parms: any): void {
    this.query = "*";
    this.pageSize = parms?.pageSize || this.pageSize;
    this.pageIndex = parms?.pageIndex || this.pageIndex;
    this.libraries = parms?.libraries || this.userService.user.properties.awlibraryObjectList.filter((x: any) => x.isAvailable).map((x: any) => x.name);
    this.type = parms.dataQueryType || "16";
    this.resultView = parms?.resultView || undefined;

    let criteria = parms.query ? parms.query.split(",") : [];
    let parsedCriteria: string[] = [];

    criteria.forEach((criterion: string) => {
      if (criterion.startsWith("qs")) {
        this.query = criterion.substring(criterion.indexOf("[") + 1, criterion.indexOf("]"));
      } else {
        parsedCriteria.push(criterion);
      }
    });

    criteria = parsedCriteria.length > 0 ? parsedCriteria.join(",") : "";
    let str = this.buildSearch(null, null, null, null, null, null, null, criteria, null, null, null);
    str += "&INVOKER=DASHBOARD";

    const headers = new HttpHeaders({
      "Accept": "application/json charset=UTF-8",
      "Content-Type": "application/json charset=UTF-8"
    });

    setTimeout(() => {
       this.blockUI.start(this.userService.LOCALIZATION.searching);
    });

    // Make the HTTP GET request
    this.http.get(str, { headers }).pipe(
      catchError(error => {
		this.broadcastEvent.emit({type:'searchEndedDashboard',data:{
            error: true
        }});  
        this.blockUI.stop();
        throw error;
      })
    ).subscribe((response: any) => {
      const results = response['csw:GetRecordsResponse']['csw:SearchResults'];

      this.results = {
        index: parms.dashboardIndex,
        records: results.iStoreRecord == null ? null : Array.isArray(results.iStoreRecord) ? results.iStoreRecord : [results.iStoreRecord]
      } as SearchResults;

      this.formatSearchResults(this.results);
      // this.searchEndedDashboardSubject.next(this.results);
      this.broadcastEvent.emit({type:'searchEndedDashboard',data:this.results});
      this.blockUI.stop();
    });
  }
     
  formatSearchResults(results: SearchResults): void {
    function decode(text: any): any {
        if (typeof text !== 'string') return text;
        return text.replace(/&#(\d+);/g, (_match, dec) => String.fromCharCode(dec));
    }

    if (results.records) {
        results.records.forEach((record: any) => {
            //////////////////////////////////////////////
            // Cache decoded/trusted known values for views
            record._decoded = {};
            record._trusted = {};

            const trustme = [
                'abstract',
                'alternative',
                'comments',
                'db_description', 'description',
                'doc_summary',
                'rights',
                'summary_text',
                'title'
            ];

            // Title is a special case
            record._title = record.title;
            record.title = this.trustHtml(record.title, false);

            Object.entries(record).forEach(([key, value]) => {
                if (key.startsWith('_')) return;
                record._decoded[key] = decode(value);
                if (trustme.includes(key)) {
                    record._trusted[key] = this.trustHtml(decode(value), false);
                }
            });
            // End cache
            //////////////////////////////////////////////

            record._accessLevel = this.userService.getProductAccessLevel(record);

            if (record.library) {
                const libraries = this.userService.user?.properties?.awlibraryObjectList || [];
                const foundLibrary = libraries.find((lib: any) => lib.name === record.library);
                if (foundLibrary) {
                    record.library = foundLibrary.displayName;
                }
            }
        });
    }

    if (results.facets) {
        if (!Array.isArray(results.facets)) {
            results.facets = [results.facets];
        }

        results.facets.forEach((facet: any) => {
            facet.solrfieldname = facet.name;
            if (facet.name.endsWith('_s')) {
                facet.solrfieldname = facet.name.substring(0, facet.name.lastIndexOf('_s'));
            } else if (facet.name.endsWith('_ss')) {
                facet.solrfieldname = facet.name.substring(0, facet.name.lastIndexOf('_ss'));
            }

            const typeList = this.userService.user?.properties?.awfieldCollectionObjectList?.filter((x: any) => x.field === facet.solrfieldname) || [];
            const typeMap: { [key: string]: string } = {};

            if (typeList.length > 0 && Array.isArray(typeList[0].element)) {
                typeList[0].element.forEach((x: any) => {
                    typeMap[x.value] = x.name;
                });
            }

            if (!Array.isArray(facet.value)) {
                facet.value = facet.value == null ? [] : [facet.value];
            }

            const currType = this.userService.user?.searchInputs?.find(x =>
                x.name === facet.name || `${x.name}_s` === facet.name || `${x.name}_ss` === facet.name
            );

            facet.value = facet.value.map((v: string) => {
                const count = v.substring(v.lastIndexOf('[') + 1, v.lastIndexOf(']'));
                const trueValue = v.substring(0, v.lastIndexOf('['));
                let quotifiedValue = `"${trueValue}"`;
                const display = typeMap[trueValue] || trueValue;

                if (currType && ['LONG', 'INTEGER', 'FLOAT', 'DATE'].includes(currType.type) && trueValue.includes(' - ')) {
                    // Do not use quotes for range facets
                    quotifiedValue = trueValue;
                }

                return {
                    display: `${display} [${count}]`,
                    displayName: display,
                    count: count,
                    value: trueValue,
                    quotifiedValue: quotifiedValue
                };
            });
        });
    }
  }
/** Main consolidated function to process search results for controller display. **/
 processSearchResults(searchResults: SearchResults, validFields: any[] = []): any {
	    let results = cloneDeep(searchResults);
        const linkFields = validFields.filter(x => x.isLink);
        const dateFormatDisplay = this.userService.getDateFormat();
        let index = 1 + ((this.pageIndex - 1) * this.pageSize);

        const records = results.records.map((x: any) => {
          x._index = index++;
          x._rank = { value: x.aw_rank, visible: results.show_rank };
          x._thumbs = formatString(this.configService.getConfig().record.thumbnailURL,x.id);
          if (x.entity_type_id) {
            x._thumbs = x._thumbs + '?type=' + x.entity_type_id;
          }
          x._rm_completed_bool = x.rm_completed === "Complete";
          x._accessLevel = this.userService.getProductAccessLevel(x);
          x.titles = {};

          linkFields.forEach(y => {
            if (x[y.name] && !(x[y.name] instanceof Array)) {
              const links = x[y.name].split(";");
              const processedLinks = links.map((link: string) => {
                if (link.indexOf('||') > -1) {
                  const pairs = link.split('||');
                  return pairs.length === 2 ? { name: pairs[0], href: pairs[1] } : { name: link, href: link };
                } else {
                  return { name: link, href: link };
                }
              });
              x[y.name] = processedLinks;
              x.titles[y.name] = this._customHtml(processedLinks);
            }
          });
		  if(this.configService.getConfig().search?.searchableResultFields){	
	          this.configService.getConfig().search.searchableResultFields.forEach((field: any) => {
	            if (x[field]) {
	              x[field] = typeof x[field] === 'string' ? x[field].split(";") : [x[field]];
	              x.titles[field] = x[field];
	            }
	          });
          }

          validFields.forEach(field => {
            if (field.type === "DATE" && x[field.name]) {
              x[field.name] = dayjs(x[field.name]).format(dateFormatDisplay);
              x._decoded[field.name] = dayjs(x[field.name]).format(dateFormatDisplay);
              x.titles[field.name] = x[field.name];
            } else if (typeof x[field.name] === 'string' && x[field.name]) {
              x[field.name] = x[field.name].replace(/; /g, ';').replace(/;/g, '; ');
              x.titles[field.name] = x[field.name];
            }
          });

          return x;
        });
        return records;
  }

  private _customHtml(links: any[]): string {
    // Implement your custom HTML generation logic here
    return links.map(link => `${link.name}:${link.href}`).join('\n');
  }

  getSearchObjectFromHistory(historyObj: any):any {
    // format the history searchObj into a similar data structure as a saved search
    // as expected by the receiving service;
    let searchObj = {
        data: {
            query: historyObj.qs,
            criteria: {} as Record<string, any>,
            libraries: {} as Record<string, string>,
            type: 16,
            aoi: null
        }
    };
    if (historyObj.qs == 'Wildcard (*)') {
        searchObj.data.query = '*';
    }
    let libs = (historyObj.library) ? historyObj.library.split(',') : [];
    libs.forEach((lib: string) => {
        searchObj.data.libraries[lib] = "foo"; // Throwaway value
    });

    let type = historyObj.type;
    switch (type) {
        case "Exact":
            searchObj.data.type = 0;
            break;
        case "Boolean":
            searchObj.data.type = 16;
            break;
        case "Concept":
            searchObj.data.type = 32;
            break;
        case "Pattern":
            searchObj.data.type = 64;
            break;
    }

    const c = historyObj.criteria ? historyObj.criteria : [];
    c.forEach((criteria: any) => {
        if (criteria.name === "geo") {
            searchObj.data.aoi = criteria.value;
        } else {
            searchObj.data.criteria[criteria.name] = {
                searchParam: `${criteria.name}=${criteria.value}`,
                display_value: criteria.value,
                display_header: criteria.label
            };
        }
    });
    
    return searchObj;
  };
  
  async getUserSearchHistory(excludeBrowse: boolean = false): Promise<any[]> {
	  const controller = new AbortController();
	  const timeoutId = setTimeout(() => controller.abort(), 10000);
	  let response: Response | null = null;
  	  return new Promise((resolve, reject) => {  
		this.ngZone.runOutsideAngular(async () => {
			try {  
		const url = `/aw-server/rest/search/lastUserSearches?nocache=${Date.now()}`;
	    response = await fetch(url, {
	      signal: controller.signal,
	      credentials: 'include',
	      headers: {
	        'Connection': 'close',
	        'Pragma': 'no-cache'
	      }
	    });
	   
	
	    clearTimeout(timeoutId);
	
	    if (!response.ok) {
	      await response.text().catch(() => {});
	      throw new Error(`HTTP ${response.status}`);
	    }
	
	    // Clone response before processing to avoid stream lock
	    const responseClone = response.clone();
	    const data: any[] = await responseClone.json();
	    const userFields = this.userService.user?.properties?.awfieldObjectList || [];
	
	    // Process data immutably
	    const processedData = data.map(item => {
	      const newItem = { ...item };
	      const created = new Date(newItem.auditDate);
	      newItem.created = `${created.toLocaleDateString('en-US')} ${created.toLocaleTimeString('en-US')}`;
	      newItem.createdTime = created.toLocaleTimeString('en-US');
	
	      try {
	        if (newItem.args) {
	          newItem.dataObj = JSON.parse(newItem.args);
	          newItem.total = newItem.dataObj.total;
	          newItem.library = newItem.dataObj.library;
	          newItem.type = newItem.dataObj.qtype;
	          newItem.qs = newItem.dataObj.qs || "*";
	          
	          delete newItem.dataObj.total;
	          delete newItem.dataObj.library;
	          delete newItem.dataObj.qtype;
	          delete newItem.dataObj.qs;
	
	          if (newItem.qs === "*") {
	            newItem.qs = "Wildcard (*)";
	          }
	
	          newItem.criteria = [];
	          Object.keys(newItem.dataObj).forEach(k => {
	            if (k !== "SESSID" && k !== "durationMilisec") {
	              const ufield = userFields.find((x: any) => x.name === k);
	              newItem.criteria.push({
	                name: k,
	                label: ufield?.displayName || k,
	                value: newItem.dataObj[k]
	              });
	              if (k === "facet-fields") newItem.isBrowse = true;
	            }
	          });
	        }
	      } catch (e) {
	        newItem.dataObj = {};
	      }
	
	      return newItem;
	    });
        this.ngZone.run(() => resolve(excludeBrowse 
	      ? processedData.filter(item => !item.isBrowse) 
	      : processedData));
	  } catch (error) {
	    console.error('Request failed:', error);
	    throw error;
	  } finally {
	    clearTimeout(timeoutId);
	  }
	    });  
	    
	    });
	  
	} 
  private processSearchData(data: any[], excludeBrowse: boolean): any[] {
	  const userFields = this.userService.user?.properties?.awfieldObjectList || [];
	  
	  return (data ?? []).map(item => {
	    const processed = { ...item };
	    const created = new Date(processed.auditDate);
	    
	    processed.created = `${created.toLocaleDateString('en-US')} ${created.toLocaleTimeString('en-US')}`;
	    processed.createdTime = created.toLocaleTimeString('en-US');
	
	    try {
	      if (processed.args) {
	        processed.dataObj = JSON.parse(processed.args);
	        // ... rest of your processing logic ...
	      }
	    } catch (e) {
	      processed.dataObj = {};
	    }
	    
	    return processed;
	  }).filter(item => !excludeBrowse || !item.isBrowse);
	}
	
  async getUserSearchHistory2(excludeBrowse: boolean = false): Promise<any[]> {
      const controller = new AbortController();
	  const timeoutId = setTimeout(() => controller.abort(), 10000);
	  let response;
	  const url = `/aw-server/rest/search/lastUserSearches?nocache=${Date.now()}`;
	  try {
	    response = await Promise.race([
	      fetch(url, {
	        signal: controller.signal,
	        credentials: 'include', // Ensure cookies are sent
	        cache: 'no-store', // Prevent cache issues
	        headers: {
			    'Connection': 'close'  // Explicitly close connection after request
			  }
	      }),
	      new Promise<never>((_, reject) => 
	        setTimeout(() => reject(new Error('Network timeout')), 10000))
	    ]);
	    clearTimeout(timeoutId);
	    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
	    const data: any[] = await response.json();
        let userFields = this.userService.user.properties.awfieldObjectList;
        (data ?? []).forEach((item: any) => {
          let created = new Date(item.auditDate);
          item.created = created.toLocaleDateString('en-US') + ' ' + created.toLocaleTimeString('en-US');
          item.createdTime = created.toLocaleTimeString('en-US');

          try {
            item.dataObj = JSON.parse(item.args);
            item.total = item.dataObj.total; delete item.dataObj.total;
            item.library = item.dataObj.library; delete item.dataObj.library;
            item.type = item.dataObj.qtype; delete item.dataObj.qtype;
            item.qs = item.dataObj.qs; delete item.dataObj.qs;
            if (!item.qs || item.qs === "*") item.qs = "Wildcard (*)";

            item.criteria = [];
            Object.keys(item.dataObj).forEach((k: string) => {
              if (k !== "SESSID" && k !== "durationMilisec") {
                let ufield = userFields.find((x: any) => x.name === k);
                let label = ufield ? ufield.displayName : k;
                let prop = { name: k, label: label, value: item.dataObj[k] };
                item.criteria.push(prop);
                if (k === "facet-fields") item.isBrowse = true;
              }
            });
          } catch (e) {
            item.dataObj = {};
          }
        });	
        return excludeBrowse ? (data ?? []).filter((datum: any) => !datum.isBrowse) : data;	  
	  } catch (error) {
	    console.error('Request failed:', error);
	    throw error; // Re-throw for calling code to handle
	  } finally {
		if (response && !response.bodyUsed) {
	        await response.body?.cancel().catch(e => {
	          console.debug('Failed to cancel stream:', e.message);
	        });
        }
	  }
  }
  
  getUserSearchHistory1(excludeBrowse: boolean = false): Promise<any> {
	  console.log(">>START getUserSearchHistory");
	  const HTTP_TIMEOUT = new HttpContextToken<number>(() => 5000); // Default timeout of 5000ms
	  return firstValueFrom(
		  this.http.get<any[]>("/aw-server/rest/search/lastUserSearches", {
		    context: new HttpContext().set(HTTP_TIMEOUT, 5000) // Override with specific timeout
		  }).pipe(
		    catchError(err => {
		      console.error("Request failed:", err);
		      return throwError(() => err);
		    })
		  )
      )
	  .then(responsData => { 
		console.log('Success:', responsData);
		const data = responsData as any[];   
        let userFields = this.userService.user.properties.awfieldObjectList;
        (data ?? []).forEach((item: any) => {
          let created = new Date(item.auditDate);
          item.created = created.toLocaleDateString('en-US') + ' ' + created.toLocaleTimeString('en-US');
          item.createdTime = created.toLocaleTimeString('en-US');

          try {
            item.dataObj = JSON.parse(item.args);
            item.total = item.dataObj.total; delete item.dataObj.total;
            item.library = item.dataObj.library; delete item.dataObj.library;
            item.type = item.dataObj.qtype; delete item.dataObj.qtype;
            item.qs = item.dataObj.qs; delete item.dataObj.qs;
            if (!item.qs || item.qs === "*") item.qs = "Wildcard (*)";

            item.criteria = [];
            Object.keys(item.dataObj).forEach((k: string) => {
              if (k !== "SESSID" && k !== "durationMilisec") {
                let ufield = userFields.find((x: any) => x.name === k);
                let label = ufield ? ufield.displayName : k;
                let prop = { name: k, label: label, value: item.dataObj[k] };
                item.criteria.push(prop);
                if (k === "facet-fields") item.isBrowse = true;
              }
            });
          } catch (e) {
            item.dataObj = {};
          }
        });	
        return excludeBrowse ? (data ?? []).filter((datum: any) => !datum.isBrowse) : data;	  
	  })
	  .catch(err => { console.error('Error:', err); });  
	  
	  
    /*return this.http.get<any[]>("/aw-server/rest/search/lastUserSearches").pipe(timeout(5000),  // 2 seconds
      catchError(err => {
        console.error("Request timed out or failed:", err);
        return of([]);
      })).toPromise()
      .then((data) => {
		  this.ngZone.run(() => {
			  console.log(">>PROCESS data getUserSearchHistory");
		let userFields = this.userService.user.properties.awfieldObjectList;
        (data ?? []).forEach(item => {
          let created = new Date(item.auditDate);
          item.created = created.toLocaleDateString('en-US') + ' ' + created.toLocaleTimeString('en-US');
          item.createdTime = created.toLocaleTimeString('en-US');

          try {
            item.dataObj = JSON.parse(item.args);
            item.total = item.dataObj.total; delete item.dataObj.total;
            item.library = item.dataObj.library; delete item.dataObj.library;
            item.type = item.dataObj.qtype; delete item.dataObj.qtype;
            item.qs = item.dataObj.qs; delete item.dataObj.qs;
            if (!item.qs || item.qs === "*") item.qs = "Wildcard (*)";

            item.criteria = [];
            Object.keys(item.dataObj).forEach((k: string) => {
              if (k !== "SESSID" && k !== "durationMilisec") {
                let ufield = userFields.find((x: any) => x.name === k);
                let label = ufield ? ufield.displayName : k;
                let prop = { name: k, label: label, value: item.dataObj[k] };
                item.criteria.push(prop);
                if (k === "facet-fields") item.isBrowse = true;
              }
            });
          } catch (e) {
            item.dataObj = {};
          }
        });
         });
         console.log(">>END getUserSearchHistory");
         return excludeBrowse ? (data ?? []).filter(datum => !datum.isBrowse) : data;
      })
      .catch((error) => {
        console.error("Error fetching user search history", error);
        return Promise.reject(error);
      });*/
  }

  getConceptsForTerms(terms: string): Promise<string[]> {
    return firstValueFrom(this.http.get<string[]>("/aw-server/rest/search/synonyms?terms=" + terms))
    .then((data) => {
	  return data ?? [];
    }).catch((error) => {
      console.error("Error fetching synonyms", error);
      return Promise.reject(error);
    });
  };
  
  saveSearch(payload: any): Promise<boolean> {
    return firstValueFrom(this.http.post<any>(this.configService.getConfig().notifications.addSavedSearchURL, payload))
    .then((data) => {
		return data?true:false;
	}).catch((error) => {
      console.error("Error saving search", error);
      return Promise.reject(error);
    });
  };
  
  updateSearch(searchId: string, payload: any): Promise<boolean> {
    return firstValueFrom(this.http.put<any>(formatString(this.configService.getConfig().notifications.updateSavedSearchURL,searchId), payload))
    .then((data) => {
		return data?true:false;
	}).catch((error) => {
      console.error("Error to update search", error);
      return Promise.reject(error);
    });
  };

  updateSubscription(searchId: string, payload: any): Promise<boolean> {
    return firstValueFrom(this.http.put<any>(formatString(this.configService.getConfig().notifications.updateSubscriptionURL,searchId), payload))
    .then((data) => {
		return data?true:false;
	}).catch((error) => {
      console.error("Error to update search", error);
      return Promise.reject(error);
    });
  };
  getMySavedSearches(page: number, paging: any, constraints: any): Promise<any> {
    const url = `${this.configService.getConfig().notifications.userSearchNotificationsURL}?page=${page}&size=${paging.elementsPerPage}&sort=${paging.sorting.sort}&constraints=${encodeURIComponent(JSON.stringify(constraints))}`;

    return firstValueFrom(this.http.get<any>(url)).then(response => {
      if (!response || !response.content) {
        return false;
      }

      response.content.forEach((item: any) => {
        const created = new Date(item.created);
        item.created = created.toLocaleDateString('en-US') + ' ' + created.toLocaleTimeString('en-US');
        item.createdDate = created.toLocaleDateString('en-US');

        try {
          item.dataObj = JSON.parse(item.data);
          const type = Number(item.dataObj.type);
          switch (type) {
            case 0:
              item.dataObj.type = "Exact";
              break;
            case 16:
              item.dataObj.type = "Boolean";
              break;
            case 32:
              item.dataObj.type = "Concept";
              break;
            case 64:
              item.dataObj.type = "Pattern";
              break;
          }
        } catch (e) {
          item.dataObj = {};
        }
      });

      return response;
    }).catch(() => {
      return null;
    });
  }
   
   getMySavedSearchesFiltered(page: number, paging: any, constraints: any): Promise<any> {
    const url = `${this.configService.getConfig().notifications.validPublicSearchesURL}?page=${page}&size=${paging.elementsPerPage}&sort=${paging.sorting.sort}&constraints=${encodeURIComponent(JSON.stringify(constraints))}`;

    return firstValueFrom(this.http.get<any>(url)).then(response => {
      if (!response || !response.content) {
        return false;
      }

      response.content.forEach((item: any) => {
        const created = new Date(item.created);
        item.created = created.toLocaleDateString('en-US') + ' ' + created.toLocaleTimeString('en-US');
        item.createdDate = created.toLocaleDateString('en-US');

        try {
          item.dataObj = JSON.parse(item.data);
          const type = Number(item.dataObj.type);
          switch (type) {
            case 0:
              item.dataObj.type = "Exact";
              break;
            case 16:
              item.dataObj.type = "Boolean";
              break;
            case 32:
              item.dataObj.type = "Concept";
              break;
            case 64:
              item.dataObj.type = "Pattern";
              break;
          }
        } catch (e) {
          item.dataObj = {};
        }
      });

      return response;
    }).catch(() => {
      return null;
    });
  }
 
  getAllSavedSearched(page: number, paging: any, constraints: any): Promise<any> {
    const url = `${this.configService.getConfig().notifications.validPublicSearchesURL}?page=${page}&size=${paging.elementsPerPage}&sort=${paging.sorting.sort}&constraints=${encodeURIComponent(JSON.stringify(constraints))}`;

    return firstValueFrom(this.http.get<any>(url)).then(response => {
      if (!response || !response.content) {
        return false;
      }

      response.content.forEach((item: any) => {
        const created = new Date(item.created);
        item.created = created.toLocaleDateString('en-US') + ' ' + created.toLocaleTimeString('en-US');
        item.createdDate = created.toLocaleDateString('en-US');

        try {
          item.dataObj = JSON.parse(item.data);
          const type = Number(item.dataObj.type);
          switch (type) {
            case 0:
              item.dataObj.type = "Exact";
              break;
            case 16:
              item.dataObj.type = "Boolean";
              break;
            case 32:
              item.dataObj.type = "Concept";
              break;
            case 64:
              item.dataObj.type = "Pattern";
              break;
          }
        } catch (e) {
          item.dataObj = {};
        }
      });

      return response;
    }).catch(() => {
      return null;
    });
  }
  
  deleteSearch(searchId: number):Promise<boolean>{
    //delete my search;
    return firstValueFrom(this.http.delete<boolean>(formatString(this.configService.getConfig().notifications.deleteSavedSearchURL,searchId)))
	.then(data => {
		return data?data:false;
	}).catch((error) => {
      console.error("Error to delete search", error);
      return Promise.reject(error);
    });
  };
 
  massDeleteSearches(searchIds: number[]):Promise<boolean>{
    //delete my search;
    return firstValueFrom(this.http.post<boolean>(this.configService.getConfig().notifications.massDeleteSavedSearchURL, searchIds))
	.then(data => {
		return data?data:false;
	}).catch((error) => {
      console.error("Error to delete searches", error);
      return Promise.reject(error);
    });
  };

  getSubscribedSearches(constraint: string): Promise<any> {
    let paramsString = constraint ? "?constraints=" + encodeURIComponent(JSON.stringify(constraint)) + '&page=0&size=10' : '?page=0&size=10'
    const url = this.configService.getConfig().notifications.userSubscriptionsURL + paramsString
    return firstValueFrom(this.http.get<any>(url)).then(response => {
      if (!response || !response.content) {
        return {};
      }

      response.content.forEach((item: any) => {
        const created = new Date(item.created);
        item.created = created.toLocaleDateString('en-US') + ' ' + created.toLocaleTimeString('en-US');
        item.createdDate = created.toLocaleDateString('en-US');

        try {
          item.dataObj = JSON.parse(item.data);
          const type = Number(item.dataObj.type);
          switch (type) {
            case 0:
              item.dataObj.type = "Exact";
              break;
            case 16:
              item.dataObj.type = "Boolean";
              break;
            case 32:
              item.dataObj.type = "Concept";
              break;
            case 64:
              item.dataObj.type = "Pattern";
              break;
          }
        } catch (e) {
          item.dataObj = {};
        }
      });

      return response;
    }).catch(() => {
      return null;
    });
  }
   
  subscribeToSearch(searchId: number):Promise<boolean>{
    return firstValueFrom(this.http.post<boolean>(formatString(this.configService.getConfig().notifications.subscribeToSearchURL,searchId),{}))
	.then(data => {
		return data?data:false;
	}).catch((error) => {
      console.error("Error to subscribe to search", error);
      return Promise.reject(error);
    });
  };

  massSubscribeToSearches(searchIds: number[]):Promise<boolean>{
    return firstValueFrom(this.http.post<boolean>(this.configService.getConfig().notifications.massSubscribeToSearchURL,searchIds))
	.then(data => {
		return data?data:false;
	}).catch((error) => {
      console.error("Error to subscribe to searches", error);
      return Promise.reject(error);
    });
  };

  unsubscribeFromSearch(subscriptionId: number):Promise<boolean>{
    return firstValueFrom(this.http.post<boolean>(formatString(this.configService.getConfig().notifications.unsubscribeFromSearchURL,subscriptionId),{}))
	.then(data => {
		return data?data:false;
	}).catch((error) => {
      console.error("Error to unsubscribe from search", error);
      return Promise.reject(error);
    });
  };

  massUnsubscribeFromSearches(searchIds: number[]):Promise<boolean>{
    return firstValueFrom(this.http.post<boolean>(this.configService.getConfig().notifications.massUnsubscribeFromSearchURL,searchIds))
	.then(data => {
		return data?data:false;
	}).catch((error) => {
      console.error("Error to unsubscribe from searches", error);
      return Promise.reject(error);
    });
  }; 
  
  favoriteSearch(searchId: number):Promise<boolean>{
    return firstValueFrom(this.http.post<boolean>(formatString(this.configService.getConfig().notifications.favoriteSearchURL,searchId),{}))
	.then(data => {
		return data?data:false;
	}).catch((error) => {
      console.error("Error to save favorite search", error);
      return Promise.reject(error);
    });
  };
  massFavoriteSearch(searchIds: number[]):Promise<boolean>{
    return firstValueFrom(this.http.post<boolean>(this.configService.getConfig().notifications.massFavoriteSearchURL,searchIds))
	.then(data => {
		return data?data:false;
	}).catch((error) => {
      console.error("Error to save favorite searches", error);
      return Promise.reject(error);
    });
  };
  unfavoriteSearch(searchId: number):Promise<boolean>{
    return firstValueFrom(this.http.delete<boolean>(formatString(this.configService.getConfig().notifications.unfavoriteSearchURL,searchId)))
	.then(data => {
		return data?data:false;
	}).catch((error) => {
      console.error("Error to delete favorite search", error);
      return Promise.reject(error);
    });
  };
  massUnfavoriteSearch(searchIds: number[]):Promise<boolean>{
    return firstValueFrom(this.http.post<boolean>(this.configService.getConfig().notifications.massUnfavoriteSearchURL,searchIds))
	.then(data => {
		return data?data:false;
	}).catch((error) => {
      console.error("Error to delete favorite searches", error);
      return Promise.reject(error);
    });
  };
  
 recurseSearchParms(parms: any[], criteria: string[]): void {
  if (!parms) {
    return;
  }
  parms.forEach(searchInput => {
    switch (searchInput.type) {
      case "LONG":
      case "INTEGER":
      case "FLOAT":
      case "RADIO":
        if (searchInput.content != null) {
          criteria.push(`${searchInput.name}=${searchInput.content}`);
        }
        break;

      case "STRING":
        if (searchInput.list?.length > 0) {
          const list = searchInput.list
            .map((item: any) => item.content?.trim())
            .filter((content: string | null) => content);
          if (list.length > 0) {
           	criteria.push(`${searchInput.solrstring || searchInput.name}=${list.join("|")}`);
          }
        }
        break;
      case "DATE":
	  case "DATE_RANGE":
        if (searchInput.content != null) {
          if (Array.isArray(searchInput.content)) {
            // Handle multiple date range selections
            const multi = searchInput.content.map((range: any) => 
              `${range.startDate.startOf("day").unix()}|${range.endDate.endOf("day").unix()}`
            );
            criteria.push(`${searchInput.name}=${multi.join("|")}`);
          } else if (searchInput.content.startDate && typeof searchInput.content.startDate !== "string") {
            // Handle single date range
            const start = searchInput.content.startDate.startOf("day").unix();
            const end = searchInput.content.endDate ? searchInput.content.endDate.endOf("day").unix() : start;
            criteria.push(`${searchInput.name}=${start}|${end}`);
          } else if (searchInput.datepicker) {
            // Handle calendar input
            const [start, end] = searchInput.datepicker.selectedDates;
            if (start || end) {
              const startDate = start ? dayjs(start).startOf("day").unix() : null;
              const endDate = end ? dayjs(end).endOf("day").unix() : startDate;
              if (startDate !== null && endDate !== null) {
                criteria.push(`${searchInput.name}=${startDate}|${endDate}`);
              }
            }
          } else {
            // Default text handling
            criteria.push(`${searchInput.name}=${searchInput.content}`);
          }
        }
        break;

      case "LIST":
      case "list":
        if (Array.isArray(searchInput.content)) {
          const combinedContentValue = searchInput.content
            .map((option: any) => `"${option.value}"`)
            .join(" OR ");
          criteria.push(`${searchInput.name}=${combinedContentValue}`);
        } else if (searchInput.content?.value) {
          criteria.push(`${searchInput.name}=${searchInput.content.value}`);
        } else if (searchInput.content) {
          criteria.push(`${searchInput.name}=${searchInput.content}`);
        }
        break;

      case "LIST_MULTI":
      case "list_multi":
        if (Array.isArray(searchInput.content) && searchInput.content.length > 0) {
          const multi = searchInput.content.map((item: any) => item.value);
          criteria.push(`${searchInput.name}=${multi.join("|")}`);
        }
        break;
    }
  });
  };
 
 similaritySearch(docId: number, title: string, silent: boolean = false): Promise<any> {
    if (silent) {
      // If silent, don't trigger a global broadcast; just return results.
      const url = this.buildSearch(null, null, 1, 10, [], "*", null, `lb_document_id=[${docId}]`, "&SORTBY=aw_rank:DESC", "3", []);
      return firstValueFrom(this.http.get<any>(url))
        .then(response => {
          const results = response?.["csw:GetRecordsResponse"]?.["csw:SearchResults"];
          const records = results?.iStoreRecord
            ? (Array.isArray(results.iStoreRecord) ? results.iStoreRecord : [results.iStoreRecord])
            : null;
          console.log(records);
          return records;
        })
        .catch(error => {
          console.error(error);
          throw error;
        });
    } else {
        this.query = "*";
        this.criteria = {
	        lb_document_id: {
	          content: [{ content: docId }],
	          display_header: "More Like This",
	          display_value: title,
	          searchParam: `lb_document_id=${docId}`,
	          searchString: `lb_document_id=[${docId}]`
	        }
        };
        this.type = "3"; // MORE LIKE THIS;
        this.pageIndex = 1;
        this.sortDirection = 'DESC';
        this.sortField = 'aw_rank';
        this.doSearch(null);
        return Promise.resolve();
    };
  };
    
 sreamingQuerySearch(docId: number, title: string, type: string, displayName: string,silent: boolean = false): Promise<any> {
    if (silent) {
      // If silent, don't trigger a global broadcast; just return results.
      const url = this.buildSearch(null, null, 1, 10, [], "*", null, `lb_document_id=[${docId}]`, "&SORTBY=aw_rank:DESC", type, []);
      return firstValueFrom(this.http.get<any>(url))
        .then(response => {
          const results = response?.["csw:GetRecordsResponse"]?.["csw:SearchResults"];
          const records = results?.iStoreRecord
            ? (Array.isArray(results.iStoreRecord) ? results.iStoreRecord : [results.iStoreRecord])
            : null;
          console.log(records);
          return records;
        })
        .catch(error => {
          console.error(error);
          throw error;
        });
    } else {
        this.query = "*";
        this.criteria = {
	        lb_document_id: {
	          content: [{ content: docId }],
	          display_header: displayName,
	          display_value: title,
	          searchParam: `lb_document_id=${docId}`,
	          searchString: `lb_document_id=[${docId}]`
	        }
        };
        this.type = type; 
        this.pageIndex = 1;
        this.doSearch(null);
        return Promise.resolve();
    };
  };    

  getTermSuggestions(fid: string, val: string): Promise<string[]> {
	if (!this.configService.getConfig().search.autoSuggest.enabled) return Promise.resolve([]);
	const url = formatString(this.configService.getConfig().search.autoSuggest.url,fid, val);
	  
    return firstValueFrom(this.http.get<string[]>(url))
    .then((data) => {
	  return data ?? [];
    }).catch((error) => {
      console.error("Error fetching suggestions", error);
      return Promise.reject(error);
    });
  };

  getDictionarySuggestions(fid: string, val: string): Promise<string[]> {
	const url = formatString(this.configService.getConfig().search.autoSuggest.dictionaryUrl,fid, val);
    return firstValueFrom(this.http.get<string[]>(url))
    .then((data) => {
	  return data ?? [];
    }).catch((error) => {
      console.error("Error fetching suggestions", error);
      return Promise.reject(error);
    });
  };
  
  dashboardSearch(block: any) {
    const query = block.search?.dataQuery || "";
    let searchObj = {
        q: "*",
        criteria:"",
        v: block.view || this.resultView || "G",
        type: block.search?.dataQueryType || "16",
        libraries: block.libraries
    };
    let criteria = query.split(",");
    let parsedCriteria: string[] = [];
    criteria.forEach((item: string) => {
	  if (item.startsWith("qs")) {
	      searchObj.q = item.substring(item.indexOf("[") + 1, item.length - 1);
	  } else {
	      parsedCriteria.push(item.replace('[', '').replace(']', ''));
	  }
    });
    if (parsedCriteria.length > 0) {
        searchObj.criteria = parsedCriteria.join("~~");
    }
	searchObj = this.validateSearchStateParams(searchObj);
    this.routingService.updateSearchUrl(searchObj, false, true); 
  };
                                                                        
  private getHttpParams(obj: any): string {
	  
    let httpParams = new HttpParams();
    Object.keys(obj).forEach(key => {
     if (obj[key] !== null && obj[key] !== undefined) {
      httpParams = httpParams.append(key, obj[key]);
     }
    });
    return httpParams.toString();
  }
  trustHtml(text: string, dosanitation: boolean): string{
    if (text == null || "")
        return "&nbsp";
    if (typeof text === 'string') {
        let replaced = text.replace(/href=|style=|target=|<script|<\/script/g, "");
        if (dosanitation) {
            return this.sanitizeHtml(replaced);
        } else {
            return replaced;
        }
    }
    return text;
 };
 
 private sanitizeHtml(replaced: string): string {
    return this.sanitizer.bypassSecurityTrustHtml(replaced) as string;
 }
      
 /*private getXmlData(url: string): Promise<Document> {
    return this.http.get<string>(url).toPromise()
      .then((response:string) => new DOMParser().parseFromString(response, 'application/xml'));
 }*/
 
 getSearchObject(): any {
        let searchObj = {
          aoi:this.aoi,
          query:this.query,
          criteria:this.criteria,
          type:this.type,
          libraries:this.libraries
        }
        return JSON.stringify(searchObj);
  };
  
	/**
	 * Convenience method for triggering a user-triggered search event broadcast.
	 */
	broadcastSearchEvent(payload: any): void {
		this.clearSearches(false, false);
		this.broadcastEvent.emit({ type: 'userTriggeredSearchExecution', data: payload });
	}

	/**
	 * Convenience method for triggering a user-triggered search criteria event broadcast.
	 */
	broadcastUserTriggeredCriteriaUpdateEvent(payload: any, options: any): void {
		this.broadcastEvent.emit({ type: 'userTriggeredCriteriaUpdate', data: payload, options });
	}

	/**
	 * Convenience method for triggering a search result field broadcast.
	 */
	broadcastSearchResultFieldEvent(payload: any): void {
		this.broadcastCleanSearchesEvent();
		this.broadcastEvent.emit({ type: 'searchResultField', data: payload });
	}

	/**
	 * Convenience method for triggering a user-triggered browse criteria event broadcast.
	 */
	broadcastUserTriggeredBrowseCriteriaUpdateEvent(searchInputs: any[] | null, sortopts: any, facetsField: string): void {
		searchInputs = searchInputs ?? [];
		this.broadcastEvent.emit({ type: 'userTriggeredBrowseCriteriaUpdate', data: searchInputs, options: sortopts, facetsField });
	}

	/**
	 * Convenience method for triggering a clean search event broadcast.
	 */
	broadcastCleanSearchesEvent(): void {
		this.broadcastEvent.emit({ type: "cleanSearches", data: {} });
	}

	/**
	 * Convenience method for triggering a clear search event broadcast.
	 */
	broadcastClearSearchEvent(): void {
		this.clearSearch();
		this.broadcastEvent.emit({ type: 'clearSearch', data: {} });
	}

	/**
	 * Convenience method for triggering a switch viewer mode event broadcast.
	 */
	broadcastSwitchViewerModeEvent(payload: any): void {
		this.broadcastEvent.emit({ type: 'switchViewerMode', data: payload });
	}
	
	broadcastBrowseLinkEvent(payload: any): void {
		this.broadcastCleanSearchesEvent();
		this.broadcastEvent.emit({ type: 'openBrowseLink', data: payload });
	}
	
	getSearchQueryParam(name: string): string | null {
		const queryString = window.location.search;
		const params = new URLSearchParams(queryString);
		return params.get(name);
	}

  doAutoLoginSearch(data: any, criteria: string | null): void{
	this.ngbModal.dismissAll();
    let sp = this.parseSearchParams(data, criteria);
    if (sp.isview) {
		// Ensure documentId is a valid number
        const documentId = sp.documentId ? parseInt(sp.documentId, 10) : null;
		this.routingService.updateSearchUrl({ 'docid': documentId?.toString() ?? null }, true, true);
		return;
    } else if (sp.queryString || (sp.keywords && Object.keys(sp.keywords).length > 0)) {
        let qs = sp.queryString && sp.queryString.length > 0 ? sp.queryString : '*';
        let browseFlag = 0;
        let sep = '';
        let criteriaString = Object.keys(sp.keywords || {})
            .map(key => `${key}=${sp.keywords[key]}`)
            .join('~');
        let a = 'c';
        if (sp.isbrowse) {
            browseFlag = 1;
            a = 'b';
        }
        let params = {
		  q: qs?.toString() ?? null,
		  type: '16', // convert number to string
		  criteria: criteriaString || null,
		  sort: sp.sortBy || null,
		  b: browseFlag?.toString() ?? null,
		  a: a?.toString() ?? null
		};
		
		params = this.validateSearchStateParams(params);
		this.routingService.updateSearchUrl(params, true, true);
    } else {
        const route = sp.menu === 'on' ? '/s' : '';
        this.router.navigate([route]);
    }
  }
  
  parseSearchParams (data: any, criteria: string | null): SearchParams {
    let input = criteria && criteria.trim().length > 0 ? criteria : data.search ?? "";
    let search: SearchParams = {
        libraries: [] as string[],
        queryString: "",
        queryType: "16", // default to boolean
        keywords: {} as Record<string, string>,
        sortBy: null as string | null,
        isbrowse: false,
        isview: false,
        browseStructure: null as string | null,
        browseSort: null as string | null,
        resultsDisplay: null as string | null,
        browseEntryId: null as string | null,
        menu: "on",
        documentId: null as string | null
    };
    if (data.browse?.length > 0) {
        search.isbrowse = true;
    }
    search.menu = data.showSearchMenu ? "on" : "off";
    let sortseq = 0;
    let initialBrowsePath = '';
    let initialBrowseEntryPoint = data.browse ?? "";
    let params = new URLSearchParams(input);
    let isRootBrowse = true;
    params.forEach((value, name) => {
        value = decodeURIComponent(value);
	    switch (name) {
	      case "cl":
			  /* TODO revisit, how to define AUTHORIZATION? 
			  use this.user.properties.awlibraryObjectList? */
	        /*search.libraries.push(value === "all_lib" 
	          ? ..._map(AUTHORIZATION.getUserProperties().get('libraryProperties'), 'name') 
	          : value
	        );*/
	        
	        search.libraries.push(value);
	        break;
	      case "qs":
	        search.queryString = value;
	        break;
	      case "qt":
	        search.queryType = value;
	        break;
	      case "sf":
	        search.sortBy = value;
	        break;
	      case "nid":
	        search.isbrowse = true;
	        search.queryString = "*";
	        initialBrowsePath = value;
	        isRootBrowse = false;
	        break;
	      case "nst":
			  /*TODO browseUtils.getPathSeparator(), browseUtils not defined*/
	        search.browseStructure = value.replace(/,/g, '--');
	        break;
	      case "no":
	        search.browseSort = value;
	        break;
	      case "beid":
	        search.browseEntryId = value;
	        break;
	      case "disp":
	        search.resultsDisplay = value;
	        break;
	      case "menu":
	        search.menu = value;
	        break;
	      case "docid":
	        search.isview = true;
	        search.documentId = value;
	        break;
	      case "browse":
	        initialBrowseEntryPoint = value;
	        break;
	      default:
	        search.keywords[name] = value;
	        break;
	    }                
        if (search.isbrowse) {
          if (isRootBrowse) {
            search.keywords["initial-browse-path"] = `${initialBrowseEntryPoint}~ROOT`;
          } else if (initialBrowsePath && initialBrowsePath.length > 0) {
            search.keywords["initial-browse-path"] = `${initialBrowseEntryPoint}~${initialBrowsePath}`;
          }
        }
    });
    return search;
  }
  
  setBrowseConfig(config: any): void{
	  this.browseConfig = config;
  }
  

  broadcastCustomEvent(eventType: string, payload: any): void {
	this.clearSearches(false, false);
	this.broadcastEvent.emit({ type: eventType, data: payload });
  } 
}